/**
 * FeatureFlagRoute
 *
 * Guards a route behind a per-institute feature flag. Unlike the
 * `useInstituteFeatureFlag` hook (which is fail-open / returns true while
 * loading), this component suspends rendering until the flag data arrives so
 * that gated pages never briefly mount and fire API calls before redirecting.
 *
 * Behaviour:
 *   - Loading  → renders nothing (avoids premature 403 API calls)
 *   - Flag disabled → redirects to dashboard
 *   - Flag enabled / absent → renders children (opt-out / fail-open model)
 */

import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { INSTITUTE_FEATURE_FLAGS_QUERY_KEY } from '@/hooks/useInstituteFeatureFlag';
import { getInstituteFeatureFlags } from '@/services/api/featureFlags';
import { ROUTES } from '@/router/routes';

interface FeatureFlagRouteProps {
  featureKey: string;
  children: React.ReactNode;
}

export function FeatureFlagRoute({ featureKey, children }: FeatureFlagRouteProps) {
  const { data, isLoading } = useQuery({
    queryKey: INSTITUTE_FEATURE_FLAGS_QUERY_KEY,
    queryFn: getInstituteFeatureFlags,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return null;

  const isEnabled = data ? (data[featureKey] ?? true) : true;
  if (!isEnabled) return <Navigate to={ROUTES.DASHBOARD.ROOT} replace />;

  return <>{children}</>;
}
